/**
 * Configurable post-processing: read a value, transform it, write it somewhere.
 *
 * The rules a workflow needs were hardcoded flags — `setName` composed 2.018, `dprFromDat` copied 1.005 into
 * 2.038, `criFromOri` copied 1.008 into 2.073, `crlfFields` normalised line endings. Each new agency wanted
 * one more, and each one was a code change and a deploy, which is the wrong shape for something that varies
 * per transaction type.
 *
 * A rule is a READ, a chain of OPS, and WRITES anywhere in the chain — the same shape as the notation this
 * was specified in:
 *
 *   upper(lastName) -> write(up_last)
 *   upper(firstName) -> append(',') -> append(:up_last) -> write(2.018)
 *
 * Rules run IN ORDER and share the form values, which is what makes the second rule able to read what the
 * first wrote. A write to a form field is visible to every later rule; a write to an EBTS reference is
 * collected for the builder.
 *
 * Deliberately not an expression language. Every operation is a named step with named arguments, so a rule is
 * inspectable, diffable, and cannot do anything the list below does not describe — a configuration that can
 * run arbitrary code is a configuration nobody can safely review.
 */

/** A literal, another form field, or an EBTS field already written. */
export interface RuleRef {
  literal?: string | undefined;
  field?: string | undefined;
  ebts?: string | undefined;
}

export type RuleOp =
  | { op: 'upper' }
  | { op: 'lower' }
  | { op: 'trim' }
  /** Collapse any newline form to CRLF — what the EBTS wants for a multi-line field. */
  | { op: 'crlf' }
  | ({ op: 'append' } & RuleRef)
  | ({ op: 'prepend' } & RuleRef)
  /** Pad on the right to `length`. Longer values are left alone, never truncated. */
  | { op: 'padEnd'; length: number; pad?: string | undefined }
  /** Pad on the left to `length` — "prepend to fixed length". */
  | { op: 'padStart'; length: number; pad?: string | undefined }
  /** Replace by regular expression. `flags` defaults to `g`. */
  | { op: 'regex'; pattern: string; replace: string; flags?: string | undefined }
  | { op: 'slice'; start: number; end?: number | undefined }
  /** Write what the chain holds so far, and carry on. */
  | { op: 'write'; field?: string | undefined; ebts?: string | undefined };

/**
 * Whether a rule runs at all.
 *
 * The one thing a straight chain cannot express, and the thing every real workflow needs: pad the SSN only
 * when there is one, set the attorney field only for an attorney account, write a suffix only if the operator
 * entered one. Without it a rule either always fires — writing empty strings, which the FBI rejects as
 * violations rather than ignoring — or has to be split into a separate workflow.
 */
export interface RuleWhen {
  /** Compared against the value of this form field, or of an EBTS reference. */
  field?: string | undefined;
  ebts?: string | undefined;
  is?: 'empty' | 'notEmpty' | 'equals' | 'matches' | undefined;
  /** For `equals` / `matches`. */
  value?: string | undefined;
  /** Invert the test. */
  not?: boolean | undefined;
}

export interface WorkflowRule {
  /** Shown in the editor and in a failure message; not used for anything else. */
  label?: string | undefined;
  enabled?: boolean | undefined;
  /** Runs only when this holds. Absent means always. */
  when?: RuleWhen | undefined;
  read: RuleRef;
  ops: RuleOp[];
}

/** Whether a rule's condition holds. An unreadable condition does NOT run the rule: silence beats a guess. */
export function conditionHolds(when: RuleWhen | undefined, ctx: RuleContext): boolean {
  if (!when) return true;
  const subject = when.field ? (ctx.values[when.field] ?? '') : when.ebts ? (ctx.ebts[when.ebts] ?? '') : '';
  let held: boolean;
  switch (when.is ?? 'notEmpty') {
    case 'empty': held = subject.trim() === ''; break;
    case 'notEmpty': held = subject.trim() !== ''; break;
    case 'equals': held = subject === (when.value ?? ''); break;
    case 'matches':
      try {
        held = new RegExp(when.value ?? '').test(subject);
      } catch (err) {
        console.error(`Workflow rule condition: regex "${when.value}" is not valid; rule skipped`, err);
        return false;
      }
      break;
    default: return false;
  }
  return when.not ? !held : held;
}

export interface RuleContext {
  /** Form values. Mutated by `write` to a field, so later rules see it. */
  values: Record<string, string>;
  /** EBTS values already present, keyed by qualified reference (`2.018`). Written to by `write`. */
  ebts: Record<string, string>;
}

/** What a reference resolves to, or '' — an absent value is empty, never the literal "undefined". */
function resolve(ref: RuleRef, ctx: RuleContext): string {
  if (ref.literal !== undefined) return ref.literal;
  if (ref.field) return ctx.values[ref.field] ?? '';
  if (ref.ebts) return ctx.ebts[ref.ebts] ?? '';
  return '';
}

/**
 * One operation.
 *
 * A bad regular expression is skipped rather than thrown: these come from a configuration, and one mistyped
 * pattern must not take down every capture on the workflow. It is logged so it can be found.
 */
function step(value: string, op: RuleOp, ctx: RuleContext): string {
  switch (op.op) {
    case 'upper': return value.toUpperCase();
    case 'lower': return value.toLowerCase();
    case 'trim': return value.trim();
    case 'crlf': return value.replace(/\r?\n/g, '\r\n');
    case 'append': return value + resolve(op, ctx);
    case 'prepend': return resolve(op, ctx) + value;
    // Never truncates. A value longer than the target length is a data problem to see, not to hide.
    case 'padEnd': return value.padEnd(Math.max(0, op.length), op.pad || ' ');
    case 'padStart': return value.padStart(Math.max(0, op.length), op.pad || '0');
    case 'slice': return op.end === undefined ? value.slice(op.start) : value.slice(op.start, op.end);
    case 'regex':
      try {
        return value.replace(new RegExp(op.pattern, op.flags ?? 'g'), op.replace);
      } catch (err) {
        console.error(`Workflow rule: regex "${op.pattern}" is not valid; step skipped`, err);
        return value;
      }
    case 'write':
      if (op.field) ctx.values[op.field] = value;
      if (op.ebts) ctx.ebts[op.ebts] = value;
      return value;
    default:
      // An op from a newer configuration than this build understands. Skipped, not fatal.
      console.error('Workflow rule: unknown operation', op);
      return value;
  }
}

/**
 * Run a workflow's rules over the form values.
 *
 * Returns the EBTS values the rules produced, and mutates `values` where a rule wrote to a form field —
 * that is what "hidden form data" is: a value the operator never types, computed here and available to the
 * TCN template and to later rules.
 */
export function runRules(
  rules: WorkflowRule[] | undefined,
  values: Record<string, string>,
  ebts: Record<string, string> = {},
): Record<string, string> {
  const ctx: RuleContext = { values, ebts };
  for (const rule of rules ?? []) {
    if (rule.enabled === false) continue;
    if (!conditionHolds(rule.when, ctx)) continue;
    let v = resolve(rule.read, ctx);
    for (const op of rule.ops ?? []) v = step(v, op, ctx);
  }
  return ctx.ebts;
}
